import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { IMAGES } from "../data/images";
import CTABand from "../components/shared/CTABand";
import PriorityListModal from "../components/shared/PriorityListModal";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const NEWS_ARTICLES = [
  {
    id: "1",
    tag: "Announcement",
    date: "Coming Soon",
    title: "Core AI Summit 2027 Officially Announced for Niagara Falls, Ontario",
    excerpt: "Canada's newest AI conference and tradeshow brings together business leaders, enterprise executives, and technical builders for two days of practical AI implementation.",
    image: IMAGES.keynote,
  },
  {
    id: "2",
    tag: "Program",
    date: "Coming Soon",
    title: "Three Audience Pathways Designed to Meet You Where You Are",
    excerpt: "The Core AI Summit program architecture puts practical outcomes first — with dedicated tracks for Business & SMB, Enterprise Leadership, and Technical Builders.",
    image: IMAGES.networking,
  },
  {
    id: "3",
    tag: "Speakers",
    date: "Coming Soon",
    title: "Speaker Applications Now Open for Core AI Summit 2027",
    excerpt: "We're building a speaker program unlike any Canadian AI event before it — practitioner-first, outcome-focused, and built for real-world application.",
    image: IMAGES.workshop,
  },
  {
    id: "4",
    tag: "Expo",
    date: "Coming Soon",
    title: "Exhibitor & Sponsorship Packages Available for Core AI Summit 2027",
    excerpt: "Get in front of 1,500+ AI decision-makers. Title, Platinum, Gold, and Silver sponsorship packages — plus Startup Pods for emerging companies.",
    image: IMAGES.expo,
  },
];

export default function NewsPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <div data-testid="news-page">
      <section className="page-hero" style={{ backgroundColor: "var(--surface)" }} data-testid="news-hero">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">News & Announcements</div>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-6"
              style={{ fontFamily: "var(--font-heading)" }}>Stay in the loop.</h1>
            <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
              The latest announcements, speaker reveals, program updates, and partnership news from Core AI Summit 2027.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-py" data-testid="news-grid-section">
        <div className="container-xl">
          {/* Featured article */}
          <RevealSection>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 pb-12"
              style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="rounded-2xl overflow-hidden">
                <img src="https://static.prod-images.emergentagent.com/jobs/6bb71053-4e67-4930-bf10-a7af9cda97ea/images/87037de232135183c0b355456e0bde2424ba8092ab68ab5ec827ce48e6561326.jpeg"
                  alt="Conference audience at Core AI Summit"
                  className="w-full h-64 object-cover opacity-90 hover:opacity-100 transition-opacity" />
              </div>
              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="tag-chip">{NEWS_ARTICLES[0].tag}</span>
                  <span className="text-xs" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                    <Calendar size={11} className="inline mr-1" />{NEWS_ARTICLES[0].date}
                  </span>
                </div>
                <h2 className="text-3xl font-black tracking-tight text-white mb-4"
                  style={{ fontFamily: "var(--font-heading)" }}>
                  Niagara Falls to host Core AI Summit + Tradeshow in 2027
                </h2>
                <p className="text-base leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>{NEWS_ARTICLES[0].excerpt}</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="btn-primary w-fit"
                  data-testid="news-register-btn"
                >
                  Register for More Information <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </RevealSection>

          {/* Article grid — hidden until ready */}
        </div>
      </section>

      <CTABand
        title="Never Miss an Update"
        subtitle="Join the priority list and be the first to know when news, speakers, and tickets drop."
        primaryLabel="Join Priority List"
      />

      <PriorityListModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}
